import 'flag-icons/css/flag-icons.min.css';
import React from 'react';
import { timezoneToCountryCode } from '../../utils/timezoneToCountry';

interface CountryFlagProps {
  /** Código ISO-3166-1 alpha-2 (ej. 'AR'). Tiene prioridad sobre `timezone`. */
  iso2?: string | null;
  /** Timezone IANA; se resuelve a iso2 vía `timezoneToCountryCode`. */
  timezone?: string | null;
  /** ISO2 usado si no se resuelve `iso2`/`timezone` (ej. 'AR' para el fallback histórico). */
  fallbackIso2?: string | null;
  /** Tamaño de la bandera en cualquier unidad CSS. Default '1em' (hereda del contexto). */
  size?: string;
  /** Si true (default), la bandera es cuadrada; si false, mantiene el ratio 4:3. */
  squared?: boolean;
  className?: string;
  title?: string;
}

/**
 * Bandera de país renderizada con `flag-icons` (SVG bundleado, sin CDN).
 *
 * Reemplaza al emoji Unicode de banderas, que no se renderiza en Windows.
 * Es puramente decorativa: siempre `aria-hidden`. Devuelve `null` si el código
 * no es un ISO-3166-1 alpha-2 válido o es el pseudo-código 'ZZ'.
 */
export function CountryFlag({
  iso2,
  timezone,
  fallbackIso2,
  size = '1em',
  squared = true,
  className = '',
  title,
}: CountryFlagProps) {
  const code = (iso2 || (timezone ? timezoneToCountryCode(timezone) : null) || fallbackIso2 || '').toUpperCase();
  if (!/^[A-Z]{2}$/.test(code) || code === 'ZZ') return null;
  return (
    <span
      className={`fi fi-${code.toLowerCase()}${squared ? ' fis' : ''} ${className}`.trim()}
      title={title}
      aria-hidden="true"
      style={{ fontSize: size, borderRadius: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
    />
  );
}

export default CountryFlag;
